import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { Quote, Plus, X, Phone, Calendar, ArrowRight, ExternalLink, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const OCDAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
      {items.map((item, index) => (
        <div key={item.title} className="border-b border-[#E8DDCE]">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full py-6 flex justify-between items-center text-left group cursor-pointer"
          >
            <h3 className="text-xl md:text-2xl font-serif text-[#3B352F] group-hover:text-[#123F13] transition-colors">
              {item.title}
            </h3>
            <div className="text-[#123F13] transition-transform duration-300">
              {openIndex === index ? <X size={24} /> : <Plus size={24} />}
            </div>
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="pb-8 pr-4">
                  <p className="text-[#3B352F]/70 leading-relaxed text-base md:text-lg">
                    {item.content}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const FAQAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="bg-white rounded-xl border border-[#E8DDCE] overflow-hidden shadow-sm">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full p-6 flex justify-between items-center text-left group cursor-pointer"
          >
            <h3 className="text-lg font-bold text-[#123F13]">
              {item.question}
            </h3>
            <div className="text-[#8C2F12] transition-transform duration-300">
              {openIndex === index ? <X size={20} /> : <Plus size={20} />}
            </div>
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="px-6 pb-6 text-[#3B352F]/70 leading-relaxed">
                  {item.answer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const OCD = () => {
  const canonicalUrl = "https://corehypnose.dk/ocd";
  
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "OCD-behandling med Metakognitiv Terapi og Hypnose",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Core Hypnose v/ Lars Nissen Corell – Psykoterapeut MPF",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Valby Tingsted 7",
        "addressLocality": "Valby",
        "postalCode": "2500",
        "addressCountry": "DK"
      },
      "telephone": "+4527204671"
    },
    "description": "Effektiv OCD-behandling i Valby og online. Slip tvangen og genfind roen med metakognitiv terapi og hypnose hos Lars Nissen Corell – Psykoterapeut MPF.",
    "areaServed": "Danmark",
    "serviceType": "Psykoterapi"
  };

  return (
    <ServiceLayout 
      title="OCD-behandling - Slip tvangen og genfind roen"
      subtitle="Metakognitiv terapi og hypnose hjælper dig med at slippe angsten for dine egne tanker og få kontrol over ritualerne – så du kan leve frit igen."
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/4186069a-5b75-49dc-9b42-20f6507052a5.png"
      description="Søger du hjælp til at bryde OCD-mønstret? Hos Core Hypnose hjælper jeg både børn og voksne med at slippe tvangstanker og ritualer gennem evidensbaserede metoder."
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
    >
      <Helmet>
        <title>OCD-behandling i Valby & Online | Slip tvangen hos Core Hypnose</title>
        <meta name="description" content="Slip fri af OCD med metakognitiv terapi og hypnose. Professionel behandling af tvangstanker og ritualer i Valby og online. Over 26 års erfaring." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="OCD-behandling i Valby & Online | Core Hypnose" />
        <meta property="og:description" content="Genvind friheden i hverdagen og slip tvangstankerne med effektiv behandling hos Lars Nissen Corell – Psykoterapeut MPF." />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Hvad er OCD sektion */}
      <section className="mb-16">
        <div className="w-full">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Hvad er OCD?</h2>
          <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg mb-12">
            <p>
              OCD (Obsessiv Kompulsiv Disorder) er en angstlidelse, hvor tvangstanker – ofte uønskede og påtrængende "Hvad nu hvis..."-tanker – udløser et intenst ubehag. For at dæmpe dette ubehag tyr man til tvangshandlinger eller ritualer.
            </p>
            <p>
              Det er ikke tankerne i sig selv, der er problemet, men den betydning du tillægger dem, og den måde du reagerer på dem. Jo mere du forsøger at kontrollere eller neutralisere tankerne gennem ritualer, jo stærkere og mere påtrængende bliver de.
            </p>
            <p className="font-medium text-[#123F13]">
              Behandlingen hos Core Hypnose fokuserer på at bryde denne onde cirkel, så du kan genvinde kontrollen og friheden i din hverdag.
            </p>
          </div>

          {/* Testimonial Section */}
          <div className="bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden mb-16">
            <Quote className="absolute top-8 right-8 text-white/10" size={120} />
            <div className="relative z-10 max-w-2xl">
              <h3 className="text-2xl font-serif mb-6 italic">“Jeg fik min tid og min frihed tilbage”</h3>
              <p className="text-white/80 mb-6 leading-relaxed">
                Lone på 28 år kæmpede med ritualer omkring håndvask og gentagne tjek af døre. Det fyldte alt i hendes hverdag og gjorde det svært at passe sit job.
                Efter et forløb med metakognitiv terapi og hypnose lærte hun at lade tvangstankerne være i fred.
                “Det var en kæmpe lettelse at opdage, at jeg ikke behøvede at vaske hænder 20 gange for at føle mig tryg. Nu kan jeg endelig fokusere på de ting, der betyder noget for mig.”
              </p>
              <p className="font-bold">— Lone, 28 år</p>
              <p className="text-white/60 text-sm mt-4 italic">Lone slap tvangsmønstrene – og det kan du også.</p>
            </div>
          </div>

          {/* OCD symptoms accordion */}
          <div className="py-16 border-t border-[#E8DDCE] mt-16">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="text-[#8C2F12] text-xs font-semibold tracking-[0.15em] uppercase mb-4 block">Symptomer</span>
              <h2 className="text-4xl md:text-5xl font-serif text-[#123F13] mb-6">OCD-symptomer og udtryk</h2>
              <p className="text-[#3B352F]/70 text-lg">
                OCD kan vise sig på mange måder. Her er nogle af de mest almindelige former for tvangstanker og handlinger.
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <OCDAccordion items={[
                {
                  title: "Tvangstanker",
                  content: "Ufrivillige, ubehagelige og ofte skræmmende billeder eller idéer, der popper op i bevidstheden. Det kan handle om frygt for smitte, frygt for at skade andre, eller bekymringer om at have glemt noget vigtigt."
                },
                {
                  title: "Tvangshandlinger",
                  content: "Ritualer som at tjekke døre og komfur gentagne gange, overdreven håndvask, tælleritualer eller at skulle gøre ting i en bestemt rækkefølge for at neutralisere angsten."
                },
                {
                  title: "Bekymringer om fare",
                  content: "Konstante tanker om potentielle katastrofer som brand, indbrud eller at blive smittet med en alvorlig sygdom. Tankerne føles så virkelige, at man føler sig tvunget til at handle på dem."
                },
                {
                  title: "Symmetri og orden",
                  content: "Et ekstremt behov for at ting skal stå på en helt bestemt måde, eller at alt skal være symmetrisk. Hvis balancen brydes, udløser det et voldsomt indre ubehag."
                },
                {
                  title: "Magisk tænkning",
                  content: "Overbevisningen om, at bestemte tal, farver eller handlinger kan forhindre ulykker eller styre begivenheder i virkeligheden. F.eks. 'Hvis jeg ikke træder på stregerne, sker der ikke min familie noget'."
                },
                {
                  title: "Skam og isolation",
                  content: "Mange med OCD føler stor skam over deres tanker og ritualer. Det fører ofte til hemmeligholdelse og social isolation, fordi man bruger utrolig meget energi på at skjule sin OCD for omverdenen."
                }
              ]} />
            </div>
          </div>

          {/* Why it works section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 py-16 border-t border-[#E8DDCE]">
            <div>
              <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor får man OCD?</h2>
              <div className="space-y-4 text-[#3B352F]/70">
                <p>
                  OCD opstår ofte som en mestringsstrategi for at håndtere usikkerhed og angst. Problemet er, at hjernen lærer, at ritualerne er den eneste måde at overleve ubehaget på.
                </p>
                <p>
                  Ved at forstå, at tanker blot er tanker – og ikke nødvendigvis sandheder eller ordrer – kan du begynde at ændre din reaktion. Behandlingen arbejder med at mindske den betydning, du tillægger dine tvangstanker.
                </p>
              </div>
              <div className="mt-8 p-6 bg-[#FAF7F2] rounded-xl border border-[#E8DDCE]">
                <p className="text-[#123F13] font-medium mb-4">Oplever du også søvnbesvær eller træthed i forbindelse med din OCD?</p>
                <Link to="/behandlinger" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2">
                  Læs om behandling af søvnproblemer <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl border border-[#E8DDCE] shadow-sm">
              <h3 className="text-xl font-bold text-[#123F13] mb-4">Hvorfor OCD-behandlingen virker</h3>
              <p className="text-[#3B352F]/70 mb-6">
                Hos Core Hypnose arbejder jeg med her-og-nu-strategier. Jeg træner dig i at stoppe tvangscirklen og genvinde kontrollen over din opmærksomhed.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Lær at lade tankerne passere uden at handle",
                  "Mærk roen i kroppen uden brug af ritualer",
                  "Genvind friheden til at tænke frit",
                  "Bryd isolationen og skammen"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#3B352F]/70">
                    <CheckCircle2 size={18} className="text-[#123F13] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-[#3B352F]/70 italic">
                Målet er ikke blot at dæmpe symptomerne, men at give dig redskaberne til at leve et liv, hvor OCD'en ikke længere sidder i førersædet.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="space-y-24">
        {/* Sådan foregår det sektion */}
        <section className="py-16 border-t border-[#E8DDCE]">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-12">Sådan foregår behandlingen mod OCD</h2>
          <div className="space-y-12">
            {[
              { title: "Forstå dine tvangstanker", content: "Jeg starter med at afmystificere OCD, så du forstår, hvorfor tankerne bliver ved med at komme, og hvordan du kan forholde dig anderledes til dem." },
              { title: "Metakognitive strategier", content: "Du lærer at give slip på kontrol og ritualer, og opdager gennem praktiske øvelser, at tankerne ikke er farlige, selvom de føles ubehagelige." },
              { title: "Hypnose for dyb ro", content: "Hypnosen hjælper dig med at finde ro i nervesystemet og mindske det automatiske, kropslige behov for at handle på tvangen." },
              { title: "Trin-for-trin eksponering", content: "Vi arbejder i dit tempo med at trappe ned i ritualerne. Du lærer at stå i ubehaget uden at udføre handlingen, indtil angsten lægger sig af sig selv." },
              { title: "Byg tillid og frihed op", content: "Behandlingen hjælper dig med at skabe en ny hverdag, hvor du tør stole på dig selv igen, og kan leve frit uden OCD’en som konstant ledsager." }
            ].map((step, i) => (
              <div key={i} className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 bg-[#123F13] text-white rounded-full flex items-center justify-center font-bold">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#123F13] mb-2">{step.title}</h3>
                  <p className="text-[#3B352F]/70 text-lg leading-relaxed">{step.content}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <div className="pt-16 pb-0 border-t border-[#E8DDCE]">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">FAQ - svar på dine spørgsmål om OCD</h2>
            <p className="text-[#3B352F]/70">
              Her finder du svar på nogle af de mest almindelige spørgsmål omkring OCD-behandling hos Core Hypnose.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <FAQAccordion items={[
              {
                question: "Hvor mange sessioner har jeg brug for?",
                answer: "Det er individuelt, men mange mærker en markant forandring efter 5-10 sessioner. OCD kræver ofte lidt mere tid end almindelig angst, da behandlingen fokuserer på at bryde indgroede vaner og ritualer."
              },
              {
                question: "Kan børn også få behandling for OCD?",
                answer: "Ja, jeg arbejder både med børn, unge og voksne. Metoderne tilpasses barnets alder og modenhed, så det bliver en tryg og forståelig proces."
              },
              {
                question: "Virker hypnose på OCD?",
                answer: "Hypnose er et fantastisk supplement til den metakognitive terapi. Det hjælper med at dæmpe den indre uro og skabe en dybere accept af, at tankerne ikke er farlige."
              },
              {
                question: "Hvad hvis mine tvangstanker er meget 'mærkelige' eller pinlige?",
                answer: "Tvangstanker kan handle om alt muligt, og intet er for mærkeligt eller pinligt. Som terapeut har jeg hørt det meste, og vi fokuserer ikke på indholdet af tanken, men på din reaktion på den."
              },
              {
                question: "Kan jeg få behandling online?",
                answer: "Ja, online-behandling er meget effektiv til OCD. Det kan endda være en fordel at arbejde med ritualerne i dit eget hjem, hvor de typisk opstår."
              }
            ]} />
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-[#3B352F]/70 mb-4">Vil du vide mere om OCD og forskning?</p>
            <a 
              href="https://angstforeningen.dk/ocd" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:underline"
            >
              Læs mere hos Angstforeningen <ExternalLink size={16} />
            </a>
          </div>
        </div>

        {/* Related Section */}
        <section className="py-16 border-t border-[#E8DDCE]">
          <div className="max-w-6xl mx-auto text-left">
            <div className="max-w-3xl mb-12">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Måske er dette også relevant for dig</span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
                Læs videre i det, der passer til din situation
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/helbredsangst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
                <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Helbredsangst, tvivl og tjek</h3>
                <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
              </Link>
              <Link to="/metakognitiv-terapi" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
                <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Metakognitiv terapi</h3>
                <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
              </Link>
              <Link to="/angst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
                <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Angstbehandling</h3>
                <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
              </Link>
              <Link to="/tankemylder" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
                <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Tankemylder og overtænkning</h3>
                <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <div className="text-center pb-16">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at slippe tvangen og få roen tilbage?</h2>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
            Book din første session nu <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </ServiceLayout>
  );
};

export default OCD;
